import TranslatablePageContents from '@map/components/TranslatablePageContents';
import FullDialog from '@map/components/layout/FullDialog';
import { buildApiUrl } from '@map/hooks/baseApi';
import { useCurrentProfile } from '@map/state/profile';
import { makeStyles } from '@map/theme';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import useStaticPages from '@shared/hooks/useStaticPages';
import PageSlide from '@ui/PageSlide';
import axios from 'axios';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

const useStyles = makeStyles(({ spacing }) => ({
  staticContent: {
    padding: spacing(5, 6, 0),
  },
  label: {
    lineHeight: '1.5',
  },
}));

const tabs = ['termsOfUse', 'privacyPolicy'] as const;
const [defaultTab] = tabs;

type TabsProps = Omit<Props, 'onClose'>;
const PageTabs = ({ tab = defaultTab, onChangeTab }: TabsProps) => {
  const { classes } = useStyles();
  return (
    <Tabs
      value={tab}
      variant="fullWidth"
      scrollButtons={false}
      indicatorColor="primary"
      textColor="primary"
      onChange={(_ignored, value) => onChangeTab(value)}
    >
      <Tab
        value="termsOfUse"
        className={classes.label}
        label={<FormattedMessage id="registration.terms_and_conditions" />}
      />
      <Tab
        value="privacyPolicy"
        className={classes.label}
        label={<FormattedMessage id="registration.privacy_policy_tab" />}
      />
    </Tabs>
  );
};
const tabToPageMap = {
  termsOfUse: 'terms_of_use',
  privacyPolicy: 'privacy_policy',
};

type TabName = (typeof tabs)[number];
type Props = {
  tab: TabName;
  onChangeTab: (value: TabName) => void;
  onClose: () => void;
};
const MobileTermsAndConditions = (props: Props) => {
  const { tab = defaultTab, onClose } = props;
  const { classes } = useStyles();

  const tabEl = useRef<HTMLDivElement | null>(null);
  const { projectContactEmailAddress } = useCurrentProfile();
  const onTransitionEnd = () => {
    tabEl.current?.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'start' });
  };
  const translator = useCallback(async (contentId: string, locale: string) => {
    const {
      data: { data },
    } = await axios.get<{ data: string }>(
      buildApiUrl(
        `static_contents/translated/page/${tabToPageMap[contentId as TabName]}/${locale}`,
      ),
    );
    return data;
  }, []);

  const pages = useStaticPages(tabToPageMap);

  useEffect(() => {
    onTransitionEnd();
  }, [props.tab]);

  return (
    <FullDialog
      open={true}
      bgcolor="neutrals.300"
      handleClose={onClose}
      navigation={<PageTabs {...props} />}
      showHelp={false}
      slideDirection="left"
    >
      <PageSlide
        key={tab}
        direction={tab === 'termsOfUse' ? 'right' : 'left'}
        transitionProps={{ onEnter: onTransitionEnd }}
      >
        <TranslatablePageContents
          className={classes.staticContent}
          ref={tabEl}
          contents={pages || {}}
          currentId={tab}
          translator={translator}
          disclaimerText={
            <FormattedMessage
              id="google_translate.policy_disclaimer"
              values={{ support_email: projectContactEmailAddress ?? '' }}
            />
          }
        />
      </PageSlide>
    </FullDialog>
  );
};

export default MobileTermsAndConditions;
